import { Button, Input, Modal } from "@bp/ui-components";
import { useState } from "react";
import { useMutation } from "urql";
import { type FragmentType, getFragmentData, graphql } from "../../../client/gql";
import { showSuccessToast, showUserErrorToast } from "../../../utils/toast";
import css from "./ApplicationFormModal.module.scss";

// Colocated fragment: the edit form only reads these fields off an application.
const ApplicationFormModal_Application = graphql(`
  fragment ApplicationFormModal_Application on Application {
    uuid
    name
    clientId
    uri
  }
`);

const ApplicationFormModal_CreateApplication = graphql(`
  mutation ApplicationFormModal_CreateApplication($input: [ApplicationCreateInput!]!) {
    createApplications(input: $input) {
      applications {
        ...ApplicationFormModal_Application
      }
    }
  }
`);

const ApplicationFormModal_UpdateApplication = graphql(`
  mutation ApplicationFormModal_UpdateApplication(
    $where: ApplicationWhere!
    $update: ApplicationUpdateInput!
  ) {
    updateApplications(where: $where, update: $update) {
      applications {
        ...ApplicationFormModal_Application
      }
    }
  }
`);

type Props =
  | {
      mode: "create";
      application?: undefined;
      onClose: () => void;
      onSaved: (uuid: string) => void;
    }
  | {
      mode: "edit";
      application: FragmentType<typeof ApplicationFormModal_Application>;
      onClose: () => void;
      onSaved: (uuid: string) => void;
    };

export function ApplicationFormModal(props: Props) {
  const { mode, onClose, onSaved } = props;
  const application = getFragmentData(
    ApplicationFormModal_Application,
    mode === "edit" ? props.application : undefined,
  );

  const [name, setName] = useState(application?.name ?? "");
  const [clientId, setClientId] = useState(application?.clientId ?? "");
  const [uri, setUri] = useState(application?.uri ?? "");
  const [busy, setBusy] = useState(false);

  const [, createApplication] = useMutation(ApplicationFormModal_CreateApplication);
  const [, updateApplication] = useMutation(ApplicationFormModal_UpdateApplication);

  const valid = name.trim() && (mode === "edit" || clientId.trim());

  const save = async () => {
    setBusy(true);
    try {
      if (mode === "create") {
        const res = await createApplication({
          input: [
            {
              name: name.trim(),
              clientId: clientId.trim(),
              ...(uri.trim() ? { uri: uri.trim() } : {}),
            },
          ],
        });
        if (res.error) {
          throw res.error;
        }
        const created = getFragmentData(
          ApplicationFormModal_Application,
          res.data?.createApplications?.applications?.[0],
        );
        showSuccessToast(`Application „${name}" angelegt`);
        onSaved(created?.uuid ?? "");
      } else {
        const res = await updateApplication({
          where: { uuid: application?.uuid },
          update: { name: name.trim(), uri: uri.trim() || null },
        });
        if (res.error) {
          throw res.error;
        }
        showSuccessToast(`Application „${name}" gespeichert`);
        onSaved(application?.uuid ?? "");
      }
    } catch (error) {
      showUserErrorToast({ text: "Speichern fehlgeschlagen", error });
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen
      ariaHideApp={false}
      hideFooter={false}
      title={mode === "create" ? "Application erstellen" : "Application bearbeiten"}
      onRequestClose={onClose}
      footer={
        <div className={css.foot}>
          <span className={css.footHint}>
            ClientId verknüpft die Application mit einem OAuth-Client. Lizenzen & Zugriff werden auf
            der Detailseite verwaltet.
          </span>
          <div className={css.footActions}>
            <Button hierarchy="tertiary" onClick={onClose}>
              Abbrechen
            </Button>
            <Button hierarchy="primary" disabled={!valid || busy} onClick={() => void save()}>
              {mode === "create" ? "Erstellen" : "Speichern"}
            </Button>
          </div>
        </div>
      }
    >
      <div className={css.stack}>
        <div className={css.grid2}>
          <Input
            name="appName"
            label="Name"
            placeholder="z. B. Bildungsplattform"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Input
            name="appClientId"
            label="ClientId"
            placeholder="OAuth-Client wählen oder eingeben"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            disabled={mode === "edit"}
            className={css.monoInput}
          />
        </div>
        <Input
          name="appUri"
          label="URL"
          placeholder="https://app.example.de"
          value={uri}
          onChange={(e) => setUri(e.target.value)}
        />
      </div>
    </Modal>
  );
}
